import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiMaximize,
  FiMinimize,
  FiZoomIn,
  FiZoomOut,
  FiMusic,
} from "react-icons/fi";
import { getFileKind, resolveImageUrl } from "../../utils/helpers";
import FileTypeBadge from "./FileTypeBadge";

const ZOOM_STEPS = [1, 1.5, 2, 3, 4];

// Phones and tablets can't show PDFs inside an iframe, so they get an "open" button instead
const isTouchDevice = () =>
  typeof navigator !== "undefined" &&
  (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

// Office Online can only preview files it can download from a public https URL
const isPublicUrl = (url) =>
  /^https:\/\//i.test(url) && !/^https:\/\/(localhost|127\.0\.0\.1)/i.test(url);

const ToolbarButton = ({ as: Tag = "button", className = "", ...props }) => (
  <Tag
    {...(Tag === "button" ? { type: "button" } : {})}
    className={`p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:hover:bg-transparent ${className}`}
    {...props}
  />
);

const FallbackCard = ({ file, url, message }) => (
  <div className="m-auto bg-white rounded-2xl p-8 text-center max-w-sm w-full shadow-xl">
    <FileTypeBadge file={file} size="lg" className="mx-auto mb-4" />
    <p className="font-semibold text-gray-800 break-words mb-1">{file.name}</p>
    <p className="text-sm text-gray-500 mb-6">{message}</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors"
      >
        <FiExternalLink className="w-4 h-4" /> Open file
      </a>
      <a
        href={url}
        download={file.name}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
      >
        <FiDownload className="w-4 h-4" /> Download
      </a>
    </div>
  </div>
);

/**
 * Full-screen viewer for images, PDFs, office documents, audio and video.
 * files: [{ url, name?, mimeType?, kind? }] — the viewer is open whenever files is non-empty.
 */
const FileViewer = ({ files = [], startIndex = 0, title, onClose }) => {
  const isOpen = files.length > 0;
  const count = files.length;
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [imageSize, setImageSize] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    if (isOpen) setIndex(Math.min(Math.max(startIndex, 0), count - 1));
  }, [isOpen, startIndex, count]);

  useEffect(() => {
    setZoom(1);
    setImageSize(null);
  }, [index, files]);

  const close = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    onClose();
  }, [onClose]);

  const go = useCallback(
    (delta) => setIndex((i) => (i + delta + count) % count),
    [count],
  );

  // Keyboard: Esc closes, arrows move between files (when not zoomed into an image)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (zoom === 1 && count > 1 && e.key === "ArrowRight") go(1);
      else if (zoom === 1 && count > 1 && e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, zoom, count, go, close]);

  // Lock page scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Keep the zoomed image centred in the scroll area
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage || zoom === 1) return;
    stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
    stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
  }, [zoom]);

  const file = isOpen ? files[Math.min(index, count - 1)] : null;
  const kind = file ? getFileKind(file) : null;
  const url = file ? resolveImageUrl(file.url) : "";
  const label = file?.name || title || "File";
  // Show the parent title under the file name, unless it is already the heading
  const subtitle = file?.name && title && title !== file.name ? title : null;
  const canFullscreen = typeof document !== "undefined" && document.fullscreenEnabled;

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else containerRef.current?.requestFullscreen?.().catch(() => {});
  };

  const stepZoom = (direction) =>
    setZoom((z) => {
      const i = ZOOM_STEPS.indexOf(z) + direction;
      return ZOOM_STEPS[Math.min(Math.max(i, 0), ZOOM_STEPS.length - 1)];
    });

  // Width of the image when fitted to the stage — zoom levels are multiples of it
  const fittedWidth = () => {
    const stage = stageRef.current;
    if (!stage || !imageSize) return undefined;
    const scale = Math.min(stage.clientWidth / imageSize.w, stage.clientHeight / imageSize.h, 1);
    return imageSize.w * scale;
  };

  const renderContent = () => {
    switch (kind) {
      case "image": {
        const baseWidth = zoom === 1 ? undefined : fittedWidth();
        return (
          <img
            src={url}
            alt={label}
            draggable={false}
            onLoad={(e) =>
              setImageSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
            }
            onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
            className={`m-auto select-none rounded-lg ${
              zoom === 1
                ? "max-w-full max-h-full object-contain cursor-zoom-in"
                : "max-w-none cursor-zoom-out"
            }`}
            style={zoom === 1 ? undefined : { width: baseWidth ? baseWidth * zoom : `${zoom * 100}%` }}
          />
        );
      }
      case "pdf":
        return isTouchDevice() ? (
          <FallbackCard
            file={file}
            url={url}
            message="Tap “Open file” to read this PDF full screen in your device's viewer."
          />
        ) : (
          <iframe key={url} src={url} title={label} className="w-full h-full bg-white rounded-lg" />
        );
      case "office":
        return isPublicUrl(url) ? (
          <iframe
            key={url}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
            title={label}
            className="w-full h-full bg-white rounded-lg"
          />
        ) : (
          <FallbackCard file={file} url={url} message="This document can't be previewed here. Open or download it instead." />
        );
      case "text":
        return <iframe key={url} src={url} title={label} className="w-full h-full bg-white rounded-lg" />;
      case "video":
        return (
          <video key={url} src={url} controls autoPlay playsInline className="m-auto max-w-full max-h-full rounded-lg bg-black" />
        );
      case "audio":
        return (
          <div className="m-auto bg-white rounded-2xl p-8 text-center max-w-md w-full shadow-xl">
            <div className="w-16 h-16 rounded-2xl bg-pink-50 flex items-center justify-center mx-auto mb-4">
              <FiMusic className="w-8 h-8 text-pink-500" />
            </div>
            <p className="font-semibold text-gray-800 break-words mb-4">{label}</p>
            <audio key={url} src={url} controls autoPlay className="w-full" />
          </div>
        );
      default:
        return <FallbackCard file={file} url={url} message="This file type can't be previewed. Open or download it instead." />;
    }
  };

  return createPortal(
    <AnimatePresence>
      {file && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className="fixed inset-0 z-[200] bg-black flex flex-col"
        >
          {/* Toolbar */}
          <div className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2.5 bg-black/50 text-white flex-shrink-0">
            <div className="min-w-0 flex-1 pr-2">
              <p className="text-sm font-semibold truncate">{label}</p>
              {(count > 1 || subtitle) && (
                <p className="text-xs text-white/50 truncate">
                  {[count > 1 && `${index + 1} / ${count}`, subtitle]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
            </div>
            {kind === "image" && (
              <>
                <ToolbarButton onClick={() => stepZoom(-1)} disabled={zoom === 1} title="Zoom out" aria-label="Zoom out">
                  <FiZoomOut className="w-5 h-5" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => stepZoom(1)}
                  disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                  title="Zoom in"
                  aria-label="Zoom in"
                >
                  <FiZoomIn className="w-5 h-5" />
                </ToolbarButton>
              </>
            )}
            <ToolbarButton as="a" href={url} target="_blank" rel="noopener noreferrer" title="Open in new tab" aria-label="Open in new tab">
              <FiExternalLink className="w-5 h-5" />
            </ToolbarButton>
            <ToolbarButton
              as="a"
              href={url}
              download={file.name || true}
              target="_blank"
              rel="noopener noreferrer"
              title="Download"
              aria-label="Download"
            >
              <FiDownload className="w-5 h-5" />
            </ToolbarButton>
            {canFullscreen && (
              <ToolbarButton
                onClick={toggleFullscreen}
                className="hidden sm:inline-flex"
                title={isFullscreen ? "Exit full screen" : "Full screen"}
                aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
              >
                {isFullscreen ? <FiMinimize className="w-5 h-5" /> : <FiMaximize className="w-5 h-5" />}
              </ToolbarButton>
            )}
            <ToolbarButton onClick={close} title="Close (Esc)" aria-label="Close">
              <FiX className="w-6 h-6" />
            </ToolbarButton>
          </div>

          {/* Stage */}
          <div className="relative flex-1 min-h-0">
            <div
              ref={stageRef}
              className="absolute inset-0 flex overflow-auto p-2 sm:p-4"
              onClick={(e) => e.target === e.currentTarget && close()}
            >
              {renderContent()}
            </div>
            {count > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous file"
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/50 text-white/80 hover:text-white hover:bg-black/70 transition-colors"
                >
                  <FiChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next file"
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/50 text-white/80 hover:text-white hover:bg-black/70 transition-colors"
                >
                  <FiChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {count > 1 && (
            <div className="flex-shrink-0 bg-black/50 px-3 py-2.5 overflow-x-auto">
              <div className="flex gap-2 w-max mx-auto">
                {files.map((f, i) => (
                  <button
                    key={`${f.url}-${i}`}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Show ${f.name || `file ${i + 1}`}`}
                    className={`w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                      i === index ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-80"
                    }`}
                  >
                    {getFileKind(f) === "image" ? (
                      <img src={resolveImageUrl(f.url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="w-full h-full bg-white flex items-center justify-center">
                        <FileTypeBadge file={f} size="sm" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default FileViewer;
